/**
 * Tracks the page the buyer was on when they entered the checkout flow, so
 * `converted_page_url` in Stripe metadata records the checkout-INITIATING
 * page (e.g. /quiz/gytr-pre-gw1) instead of /checkout-stripe for every
 * purchase — that's what tells marketing which content or tool drove the
 * signup.
 *
 * sessionStorage is deliberate: it is per-tab and survives the Auth0
 * signup round trip (full-page redirects in the same tab), which defeats
 * `document.referrer` — after Auth0 the referrer is the Auth0 domain, not
 * the funnel page. Checkout-flow pages themselves are excluded so reloads
 * on /checkout-stripe or /thank-you never overwrite the real initiator.
 *
 * Paywall pages (/join, /member-upgrade, /account) are excluded too, so the
 * field holds the last content or tool page before checkout rather than the
 * paywall nearly every buyer passes through. A journey with no such page
 * records nothing and `converted_page_url` is omitted — the entry is still
 * carried by first_landing_page. See entry-page-exclusions.ts.
 *
 * Only the pathname is stored — query strings can carry PII and the
 * backend strips them from converted_page_url anyway.
 */
import { isExcludedInitiatingPath } from './entry-page-exclusions';

export const INITIATING_PAGE_STORAGE_KEY = 'ffh_checkout_initiating_page';

export const recordPotentialInitiatingPage = (pathname: string): void => {
  if (isExcludedInitiatingPath(pathname)) return;

  // Storage can throw in private mode / disabled-storage webviews;
  // attribution must never break navigation.
  try {
    window.sessionStorage.setItem(INITIATING_PAGE_STORAGE_KEY, pathname);
  } catch {
    // Swallow — fail soft.
  }
};

export const readInitiatingPageUrl = (): string | undefined => {
  try {
    const pathname = window.sessionStorage.getItem(INITIATING_PAGE_STORAGE_KEY);
    if (!pathname) return undefined;
    return `${window.location.origin}${pathname}`;
  } catch {
    return undefined;
  }
};
